import { useEffect, useState } from 'react';
import { CustomMetaTitle } from 'Components/CustomMetaTitle';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import {
  ISubject,
  IRecentRequests,
  IRequestCategories,
  ISubCategory,
  getPopularCategories,
  getRecentRequests,
  getRequestCategories,
  IDepartment,
} from 'API/UniversalRequest/universalRequest';
import { ContentHeader } from 'Components/ContentHeader';
import { ParentCategoriesHeaderContent } from './ParentCategoriesHeaderContent';
import { ParentCategoriesList } from './ParentCategoriesList';
import { SubCategoriesList } from './SubCategoriesList';
import { SubCategoriesHeaderContent } from './SubCategoriesHeaderContent';
import { UploadFiles } from './UploadFiles';
import { SuccessHeaderContent } from './SuccessHeaderContent';
import { useAppSelector, useIsOtpLogin } from 'Hooks';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import { getIsLawyer, getIsLawyerAndUser } from 'Utils/lawyers';
import { useIsLawyer } from 'Hooks/useIsLawyer';
import { useScrollTopView } from 'Hooks/useScrollTopView';
import { useIsLawyerArea } from 'Hooks/useIsLawyerArea';

export enum TAB_VALUE_TYPE {
  SUBJECTS,
  DEPARTMENT,
  ALL,
}

export const UniversalRequest = () => {
  const { isOtp, isAuth } = useIsOtpLogin();
  const isLawyerArea = useIsLawyerArea();
  const isLawyer = useIsLawyer();

  const [specialCategoriesData, setSpecialCategoriesData] = useState<{
    data: IRecentRequests[];
    type: string;
  }>({
    data: [],
    type: '',
  });
  const [categoriesList, setCategoriesList] = useState<{
    subjects: ISubject[];
    departments: IDepartment[];
    subSubjects: ISubCategory[];
  }>({
    subjects: [],
    departments: [],
    subSubjects: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId,
  );

  const dictionary = Dictionary[DICTIONARY.UniversalRequest];

  const params = useParams();
  const navigate = useNavigate();
  const successMatch = useMatch(
    '/universalRequest/:tabId/:categoryId/:subCategoryId/success',
  );

  const defaultTabValue =
    getIsLawyer() && (getIsLawyerAndUser() ? isLawyerArea : true)
      ? TAB_VALUE_TYPE.ALL
      : TAB_VALUE_TYPE.SUBJECTS;

  const tabValue = params.tabId ? parseInt(params.tabId) - 1 : defaultTabValue;

  const selectedCategoryId =
    params.categoryId && params.categoryId !== '0'
      ? parseInt(params.categoryId)
      : null;

  const selectedSubCategoryId = params.subCategoryId
    ? parseInt(params.subCategoryId)
    : null;

  const isSuccessPage = !!successMatch;

  useEffect(() => {
    const fetchData = async () => {
      if (isOtp || !isAuth) {
        const popularCategories = await getPopularCategories();
        if (popularCategories)
          setSpecialCategoriesData({
            data: popularCategories,
            type: 'popular',
          });
      } else {
        const recentRequests = await getRecentRequests();
        if (recentRequests && recentRequests.length > 0) {
          setSpecialCategoriesData({
            data: recentRequests,
            type: 'recent',
          });
        } else {
          const popularCategories = await getPopularCategories();
          if (popularCategories)
            setSpecialCategoriesData({
              data: popularCategories,
              type: 'popular',
            });
        }
      }
    };

    fetchData();
  }, [imposterId, isOtp, isAuth]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      const categories = await getRequestCategories();

      if (categories) {
        const sortedCategories: IRequestCategories = categories;

        setCategoriesList(sortedCategories);
        setIsLoading(false);
      }
    };
    getData();
  }, [imposterId]);

  const handleTabChange = (_: any, newValue: TAB_VALUE_TYPE) => {
    navigate(`/universalRequest/${newValue + 1}`);
  };

  const handleCategoryTitleClick = (categoryId: number | null) => {
    if (categoryId === null) {
      navigate(`/universalRequest/${tabValue + 1}`);
    } else {
      navigate(`/universalRequest/${tabValue + 1}/${categoryId}`);
    }
  };

  const handleSubCategoryClick = (
    subSubjectId: number | null,
    parentCategoryId?: number,
  ) => {
    if (!isOtp) {
      const subCategory = categoriesList.subSubjects.find(
        (subSubject) => subSubject.id === subSubjectId,
      );
      if (subCategory?.link) {
        subCategory.link[0] === '/'
          ? navigate(subCategory.link)
          : window.location.assign(subCategory.link);
        return;
      }
    }

    const resolvedCategoryId = parentCategoryId ?? selectedCategoryId;
    navigate(
      `/universalRequest/${tabValue + 1}/${resolvedCategoryId ?? 0}/${subSubjectId}`,
    );
  };

  const resetRequest = () => {
    navigate('/universalRequest');
  };

  /** Bottom-of-page passes `replace:true` so back does not pop an external referrer; header omits it (normal push). */
  const clearCategory = (isReset?: boolean, replace?: boolean) => {
    if (isReset && !isLawyer) {
      navigate('/universalRequest/1', { replace });
    } else {
      navigate(`/universalRequest/${tabValue + 1}`, { replace });
    }
  };

  const clearSubCategory = (replace?: boolean) => {
    if (selectedCategoryId !== null) {
      navigate(`/universalRequest/${tabValue + 1}/${selectedCategoryId}`, {
        replace,
      });
    } else {
      navigate(`/universalRequest/${tabValue + 1}`, { replace });
    }
  };

  const handleSuccess = () => {
    navigate(
      `/universalRequest/${tabValue + 1}/${selectedCategoryId ?? 0}/${selectedSubCategoryId}/success`,
    );
  };

  const getParentTitleById = (categoryId: number): string => {
    if (tabValue === TAB_VALUE_TYPE.SUBJECTS) {
      return (
        categoriesList.subjects.find((category) => category.id === categoryId)
          ?.title || ''
      );
    } else {
      return (
        categoriesList.departments.find(
          (category) => category.id === categoryId,
        )?.title || ''
      );
    }
  };

  const getSubtitleById = (categoryId: number) => {
    const subSubject = categoriesList.subSubjects.find(
      (subSubject) => subSubject.id === categoryId,
    );
    return subSubject;
  };

  const getChosenSubjectTitle = () => {
    const parentLabel =
      selectedCategoryId && getParentTitleById(selectedCategoryId);
    const subLabel =
      selectedSubCategoryId && getSubtitleById(selectedSubCategoryId);

    return parentLabel && subLabel && subLabel.title
      ? `${parentLabel} > ${subLabel.title}`
      : (subLabel && subLabel.title) || '';
  };

  const getSubSubject = () =>
    categoriesList.subSubjects.find(
      (subSubject) => subSubject.id === selectedSubCategoryId,
    );

  useScrollTopView({ top: 0, smoothScroll: true });

  return (
    <>
      <CustomMetaTitle
        title={`${dictionary.title} - ${dictionary.metaTitleStepOne}`}
        disablePrefix={isOtp || !isAuth ? true : false}
      />
      <ContentHeader
        isSuccessPage={isSuccessPage}
        isOtp={isOtp}
        isAuth={isAuth}
        selectedCategoryId={selectedCategoryId}
        selectedSubCategoryId={selectedSubCategoryId}
        tabValue={tabValue}
        parentId={selectedCategoryId}
        clearCategory={clearCategory}
        clearSubCategory={clearSubCategory}
      >
        {/* render parent categories header content */}
        {selectedCategoryId === null && selectedSubCategoryId === null && (
          <ParentCategoriesHeaderContent />
        )}
        {/* render sub categories header content */}
        {selectedCategoryId !== null && selectedSubCategoryId === null && (
          <SubCategoriesHeaderContent
            getParentTitleById={getParentTitleById(selectedCategoryId)}
            tabValue={tabValue}
          />
        )}
        {/* render upload files header content */}
        {selectedSubCategoryId !== null && !isSuccessPage && (
          <>
            <SubCategoriesHeaderContent
              getParentTitleById={
                categoriesList.subSubjects.find(
                  (subSubject) => subSubject.id === selectedSubCategoryId,
                )?.parentTitle ||
                (selectedCategoryId
                  ? getParentTitleById(selectedCategoryId)
                  : '')
              }
              getSubtitleObj={getSubtitleById(selectedSubCategoryId)}
              tabValue={tabValue}
            />
          </>
        )}
        {isSuccessPage && <SuccessHeaderContent tabValue={tabValue} />}
      </ContentHeader>

      <div style={{ marginBottom: '120px' }}>
        {/* render parent categories list */}
        {selectedCategoryId === null && selectedSubCategoryId === null && (
          <ParentCategoriesList
            categoriesList={categoriesList}
            specialCategoriesData={specialCategoriesData}
            tabValue={tabValue}
            handleCategoryClick={handleCategoryTitleClick}
            handleSubCategoryClick={handleSubCategoryClick}
            handleTabChange={handleTabChange}
          />
        )}
        {/* render sub categories list */}
        {selectedCategoryId !== null && selectedSubCategoryId === null && (
          <SubCategoriesList
            goBack={(isReset) => clearCategory(isReset, true)}
            selectedCategoryId={selectedCategoryId}
            categoriesList={categoriesList}
            tabValue={tabValue}
            handleSubCategoryClick={handleSubCategoryClick}
            getParentTitleById={getParentTitleById(selectedCategoryId)}
            isLoading={isLoading}
          />
        )}
        {/* render upload files */}
        {selectedSubCategoryId !== null && (
          <UploadFiles
            subId={selectedSubCategoryId}
            clearSubCategory={() => clearSubCategory(true)}
            handleSuccess={handleSuccess}
            resetRequest={resetRequest}
            titleLabel={getChosenSubjectTitle()}
            onlineFormLink={getSubSubject()?.onlineFormLink}
            isDownload={getSubSubject()?.isOnlineFormLinkForDownload}
            isOtp={isOtp}
            subCategory={getSubSubject()}
          />
        )}
      </div>
    </>
  );
};
